import { ReactNode } from "react";
import { cn } from "~/lib/utils";

interface SettingsPageProps {
  title: string;
  // The tagline under the title: what this page is for, in a sentence.
  description?: ReactNode;
  // A control aligned with the title, e.g. an "Add" button.
  action?: ReactNode;
  // Overrides the page frame. The default column suits the profile tabs.
  className?: string;
  children: ReactNode;
}

/**
 * The frame every settings page shares: a title and tagline above the cards,
 * then the cards themselves.
 *
 * A page supplies its copy and its `SettingsGroup`s and inherits the width,
 * rhythm, and heading level, so pages cannot drift apart as they are added.
 * Spending the page's one header here is also what frees a card below to hold
 * something with a header of its own — a table's column row — instead of
 * stacking two bands of chrome before the reader reaches anything real.
 */
export const SettingsPage = ({
  title,
  description,
  action,
  className,
  children,
}: SettingsPageProps) => (
  <div className={cn("mx-auto flex max-w-3xl flex-col gap-8", className)}>
    <header className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-base font-semibold text-foreground">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </header>
    {children}
  </div>
);
